"use client"

import Stack from "@mui/material/Stack"
import Box from "@mui/material/Box"
import Skeleton from "@mui/material/Skeleton"
import Container from "@mui/material/Container"
import { ProductCardSkeleton } from "@src/components"
import { PDP_THUMB_W, PDP_THUMB_H } from "./gallery-constants"

type ProductSkeletonProps = {
  loading?: boolean
  overlay?: boolean
  fullPage?: boolean
}

const InfoSkeleton = () => (
  <Box width="100%">
    <Stack direction="row" spacing={1} mb={1.5}>
      <Skeleton animation="wave" variant="rounded" width={72} height={22} />
      <Skeleton animation="wave" variant="rounded" width={56} height={22} />
    </Stack>
    <Skeleton
      animation="wave"
      variant="rectangular"
      sx={{ height: 32, width: "70%", borderRadius: "4px" }}
    />
    <Skeleton
      animation="wave"
      variant="rectangular"
      sx={{ mt: 2, height: 28, width: 120, borderRadius: "4px" }}
    />

    <Skeleton animation="wave" variant="text" sx={{ mt: 3, width: 100, height: 20 }} />
    <Skeleton
      animation="wave"
      variant="rounded"
      width={48}
      height={48}
      sx={{ mt: 1, borderRadius: "8px" }}
    />

    <Skeleton animation="wave" variant="text" sx={{ mt: 2.5, width: 48, height: 20 }} />
    <Stack direction="row" spacing={1} mt={1}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Skeleton
          key={`size-${index}`}
          animation="wave"
          variant="circular"
          width={36}
          height={36}
        />
      ))}
    </Stack>
    <Skeleton animation="wave" variant="text" sx={{ mt: 1, width: 140, height: 18 }} />

    <Skeleton animation="wave" variant="text" sx={{ mt: 2, width: 80, height: 18 }} />
    <Stack direction="row" spacing={1.5} mt={1} alignItems="center">
      <Skeleton
        animation="wave"
        variant="rectangular"
        width={120}
        height={40}
        sx={{ borderRadius: "8px" }}
      />
      <Skeleton animation="wave" variant="text" width={72} height={22} />
    </Stack>

    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={3}>
      <Skeleton
        animation="wave"
        variant="rectangular"
        sx={{ flex: 1, height: 48, borderRadius: "40px" }}
      />
      <Skeleton
        animation="wave"
        variant="rectangular"
        sx={{ flex: 1, height: 48, borderRadius: "40px" }}
      />
    </Stack>

    <Skeleton animation="wave" variant="text" sx={{ mt: 2.5, width: 160, height: 20 }} />
  </Box>
)

const GallerySkeleton = () => (
  <Stack
    direction={{ xs: "column", md: "row" }}
    gap={{ xs: 1.5, md: 2 }}
    width="100%"
    alignItems="stretch"
  >
    <Box
      sx={{
        position: "relative",
        flex: 1,
        width: "100%",
        aspectRatio: "1 / 1",
        maxWidth: { md: `calc(100% - ${PDP_THUMB_W + 16}px)` },
        minWidth: 0
      }}
    >
      <Skeleton
        animation="wave"
        variant="rectangular"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          borderRadius: "8px",
          transform: "none"
        }}
      />
    </Box>
    <Stack
      direction={{ xs: "row", md: "column" }}
      spacing={1.5}
      display={{ xs: "none", md: "flex" }}
      flexShrink={0}
    >
      {Array.from({ length: 4 }).map((_, index) => (
        <Skeleton
          key={`thumb-${index}`}
          animation="wave"
          variant="rectangular"
          sx={{
            width: PDP_THUMB_W,
            height: PDP_THUMB_H,
            borderRadius: "8px",
            flexShrink: 0,
            transform: "none"
          }}
        />
      ))}
    </Stack>
  </Stack>
)

const ProductSkeletonBody = () => (
  <Stack
    gap={{ xs: "16px", md: "32px" }}
    flexDirection={{ xs: "column", md: "row" }}
    width="100%"
    mt={2}
  >
    <Box width={{ xs: "100%", md: "55%" }} maxWidth={{ xs: "100%", md: "55%" }}>
      <GallerySkeleton />
    </Box>
    <Box width={{ xs: "100%", md: "45%" }} maxWidth={{ xs: "100%", md: "45%" }}>
      <InfoSkeleton />
    </Box>
  </Stack>
)

const ProductSkeleton = ({
  loading = true,
  overlay = false,
  fullPage = false
}: ProductSkeletonProps) => {
  if (!loading) return null

  const body = <ProductSkeletonBody />

  if (overlay) {
    return (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "#fff",
          zIndex: 9999
        }}
      >
        {body}
      </Box>
    )
  }

  if (fullPage) {
    return (
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundColor: "white",
          minHeight: "70vh"
        }}
      >
        <Box sx={{ backgroundColor: "#F9F9F9", px: { xs: 1.5, md: 2 }, py: 2 }}>
          <Container maxWidth="xl" disableGutters>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <Skeleton animation="wave" width={48} height={18} />
              <Skeleton animation="wave" width={8} height={18} />
              <Skeleton animation="wave" width={72} height={18} />
              <Skeleton animation="wave" width={8} height={18} />
              <Skeleton animation="wave" width={140} height={18} />
            </Stack>
            {body}
          </Container>
        </Box>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Stack direction="row" spacing={2} mb={2}>
            <Skeleton animation="wave" width={140} height={24} />
            <Skeleton animation="wave" width={160} height={24} />
          </Stack>
          <Skeleton
            animation="wave"
            variant="rectangular"
            sx={{ height: 96, borderRadius: "8px", mb: 4, transform: "none" }}
          />
          <Skeleton animation="wave" width={180} height={28} sx={{ mb: 2 }} />
          <Stack direction="row" spacing={2} overflow="hidden">
            {Array.from({ length: 4 }).map((_, index) => (
              <Box key={`related-${index}`} sx={{ minWidth: { xs: "45%", md: "23%" } }}>
                <ProductCardSkeleton />
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>
    )
  }

  return body
}

export default ProductSkeleton
